<template>
  <sui-segment-group
    class="sender-email"
    raised
  >
    <sui-segment
      inverted
      class="section-title"
    >
      Correo remitente (envío de correos)
    </sui-segment>

    <sui-segment :loading="loading">
      <sui-message info>
        Este es el correo desde el que se envían las facturas y notificaciones a tus clientes.
        Solo puedes tener <b>un correo remitente por cuenta</b>. Al guardarlo se genera una
        solicitud de verificación y llegará un correo de <b>SendGrid</b> a esa bandeja.
        <b>El equipo de la plataforma completa la activación por ti</b> — no necesitas una cuenta
        de SendGrid. Si te lo piden, <b>reenvía ese correo de verificación</b> al equipo. El estado
        se pondrá en verde aquí cuando quede activo.
      </sui-message>

      <!-- Current status -->
      <sui-message
        v-if="current.email && current.verified"
        positive
      >
        <sui-icon name="check circle" />
        <b>{{ current.email }}</b> está verificado y activo como remitente.
      </sui-message>
      <sui-message
        v-else-if="current.email && !current.verified"
        warning
      >
        <sui-icon name="clock outline" />
        <b>{{ current.email }}</b> está pendiente de verificación. El equipo de la plataforma la
        completará. Si te lo solicitan, <b>reenvía el correo de verificación de SendGrid</b> que
        llegó a esa bandeja (revisa también spam). Mientras tanto, los correos se envían desde
        <b>{{ current.fallback_email || "el remitente por defecto" }}</b>.
        <div class="resend-row">
          <sui-button
            size="tiny"
            :loading="resending"
            @click="resend"
          >
            Reenviar correo de verificación
          </sui-button>
          <sui-button
            size="tiny"
            basic
            :loading="loading"
            @click="load"
          >
            Ya lo verifiqué — actualizar estado
          </sui-button>
        </div>
      </sui-message>
      <sui-message
        v-else
        warning
      >
        Aún no has configurado un correo remitente. Los correos se envían desde
        <b>{{ current.fallback_email || "el remitente por defecto" }}</b>.
      </sui-message>
    </sui-segment>

    <sui-segment>
      <sui-form>
        <sui-form-field>
          <label>Correo remitente</label>
          <input
            v-model.trim="form.from_email"
            type="email"
            placeholder="facturacion@tuempresa.com"
            autocomplete="off"
          >
        </sui-form-field>

        <sui-form-field>
          <label>Nombre remitente (opcional)</label>
          <input
            v-model.trim="form.from_name"
            type="text"
            placeholder="Tu Empresa"
          >
        </sui-form-field>

        <sui-form-field>
          <label>Responder a (opcional — por defecto el mismo correo)</label>
          <input
            v-model.trim="form.reply_to"
            type="email"
            placeholder="contacto@tuempresa.com"
            autocomplete="off"
          >
        </sui-form-field>

        <details class="address-block">
          <summary>Domicilio (recomendado por SendGrid para cumplimiento)</summary>
          <sui-form-field>
            <label>Dirección</label>
            <input
              v-model.trim="form.address"
              type="text"
              placeholder="Calle y número"
            >
          </sui-form-field>
          <sui-form-field>
            <label>Ciudad</label>
            <input
              v-model.trim="form.city"
              type="text"
            >
          </sui-form-field>
          <sui-form-field>
            <label>Estado (2 letras)</label>
            <input
              v-model.trim="form.state"
              type="text"
              maxlength="2"
              placeholder="JA"
            >
          </sui-form-field>
          <sui-form-field>
            <label>C.P.</label>
            <input
              v-model.trim="form.zip"
              type="text"
            >
          </sui-form-field>
          <sui-form-field>
            <label>País</label>
            <input
              v-model.trim="form.country"
              type="text"
              placeholder="Mexico"
            >
          </sui-form-field>
        </details>
      </sui-form>
    </sui-segment>

    <sui-segment>
      <sui-message
        v-if="errorMessage"
        error
      >
        {{ errorMessage }}
      </sui-message>
      <sui-button
        positive
        :loading="saving"
        :disabled="saving"
        @click="save"
      >
        Guardar y enviar verificación
      </sui-button>
      <span class="hint">
        Al guardar, se reemplaza cualquier remitente anterior y se envía un nuevo correo de verificación.
      </span>
    </sui-segment>
  </sui-segment-group>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import { useToast } from "vue-toastification";
import Api from "../../api/Api";

const toast = useToast();
const loading = ref(true);
const saving = ref(false);
const resending = ref(false);
const errorMessage = ref("");

const current = reactive({
    email: null,
    verified: false,
    fallback_email: null,
});

const form = reactive({
    from_email: "",
    from_name: "",
    reply_to: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "",
});

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = () => {
    if (!form.from_email) return "Ingresa el correo remitente.";
    if (!emailRe.test(form.from_email)) return "El correo remitente no es válido.";
    if (form.reply_to && !emailRe.test(form.reply_to)) return "El correo de 'Responder a' no es válido.";
    return "";
};

const load = async () => {
    loading.value = true;
    try {
        const response = await Api.getSenderEmail();
        if (response.error) {
            return toast.error(response.message_client);
        }
        current.email = response.data.email || null;
        current.verified = !!response.data.verified;
        current.fallback_email = response.data.fallback_email || null;
        // Prefill the form with the current email for easy re-send/editing.
        if (current.email && !form.from_email) form.from_email = current.email;
    } catch (error) {
        toast.error("No se pudo cargar el correo remitente.");
    } finally {
        loading.value = false;
    }
};

const save = async () => {
    errorMessage.value = validate();
    if (errorMessage.value) return;

    saving.value = true;
    try {
        const response = await Api.setSenderEmail({
            from_email: form.from_email,
            from_name: form.from_name || undefined,
            reply_to: form.reply_to || undefined,
            address: form.address || undefined,
            city: form.city || undefined,
            state: form.state || undefined,
            zip: form.zip || undefined,
            country: form.country || undefined,
        });
        if (response.error) {
            errorMessage.value = response.message_client;
            return toast.error(response.message_client);
        }
        toast.success(
            `Solicitud creada para ${form.from_email}. El equipo completará la verificación; el estado se actualizará aquí.`,
        );
        await load();
    } catch (error) {
        toast.error("No se pudo guardar el correo remitente.");
    } finally {
        saving.value = false;
    }
};

const resend = async () => {
    resending.value = true;
    try {
        const response = await Api.resendSenderVerification();
        if (response.error) {
            return toast.error(response.message_client);
        }
        toast.success("Correo de verificación reenviado. Revisa tu bandeja de entrada.");
    } catch (error) {
        toast.error("No se pudo reenviar la verificación.");
    } finally {
        resending.value = false;
    }
};

onMounted(load);
</script>

<style scoped>
.sender-email {
    max-width: 760px;
    margin: auto;
}

.section-title {
    text-align: center;
    font-size: 18px;
}

.resend-row {
    margin-top: 10px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.address-block {
    margin-top: 8px;
}

.address-block summary {
    cursor: pointer;
    margin-bottom: 10px;
    color: #555;
}

.hint {
    display: block;
    margin-top: 8px;
    color: #777;
    font-size: 12px;
}
</style>
